import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
} from 'lucide-react'

import { api, esFallaDeConexion, mensajeDeError, type ErrorApi } from '@/lib/api'
import { cargarTenantPublico } from '@/lib/sesion'
import { Campo } from '@/componentes/ui/Campo'
import { PanelMarca } from './PanelMarca'

// Recuperar la contraseña.
//
// Es UNA pantalla y dos pasos, y están juntos a propósito: el enlace del mail
// cae en esta misma dirección con `?token=`, así que quien lo abre en otro
// dispositivo ve la marca del local y sabe dónde está parado. Con dos rutas
// distintas habría que mantener el mismo encabezado dos veces.
//
// El paso que pide el mail NUNCA dice si ese usuario existe: el servidor
// contesta lo mismo en los dos casos, así que la pantalla también.
export function Recuperar() {
  const { slug } = useParams<{ slug?: string }>()
  const [params] = useSearchParams()
  const token = params.get('token')

  // Los datos públicos del local, para el panel de marca. Mismo criterio que
  // el login: un local que no existe vuelve como `null`, la falla de conexión
  // se propaga.
  const consultaLocal = useQuery({
    queryKey: ['tenant-publico', slug],
    queryFn: () => cargarTenantPublico(slug!),
    enabled: !!slug,
    staleTime: Infinity,
    retry: (intentos, e) => esFallaDeConexion(e) && intentos < 10,
    retryDelay: 5000,
  })

  const tenant = consultaLocal.data ?? null
  const buscando = consultaLocal.isFetching && consultaLocal.failureCount === 0

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <PanelMarca tenant={tenant} cargando={buscando} />

      <main className="flex flex-1 items-start justify-center px-5 pb-10 pt-8 lg:items-center lg:px-12 lg:py-10">
        <div className="w-full max-w-[400px] animate-entrar">
          {token ? (
            <PasoContrasenaNueva token={token} slug={slug ?? ''} />
          ) : (
            <PasoPedirEnlace slug={slug ?? ''} />
          )}

          <div className="mt-7 border-t border-borde pt-5 text-center">
            <Link
              to={`/${slug ?? ''}/login`}
              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-verde transition-colors hover:text-verde-oscuro"
            >
              <ArrowLeft className="h-4 w-4" /> Volver a la entrada
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}

// ── Paso 1: pedir el enlace ────────────────────────────────────────────────

function PasoPedirEnlace({ slug }: { slug: string }) {
  const [email, setEmail] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const refEmail = useRef<HTMLInputElement>(null)

  useEffect(() => {
    refEmail.current?.focus()
  }, [])

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (enviando) return

    setEnviando(true)
    setError(null)

    try {
      await api.post('/auth/recuperar', { tenantSlug: slug, email: email.trim() })
      setListo(true)
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo pedir el enlace.'))
    } finally {
      setEnviando(false)
    }
  }

  // El mensaje de éxito es el MISMO exista o no ese usuario, y por eso está
  // escrito en condicional. Si dijera «te mandamos un mail», este formulario
  // sería una forma anónima de averiguar quién trabaja en el local.
  if (listo) {
    return (
      <div>
        <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-verde/25 bg-verde/[0.06] px-3.5 py-3">
          <CheckCircle2 className="mt-0.5 h-[18px] w-[18px] shrink-0 text-verde" />
          <p className="text-[13.5px] leading-snug text-tinta">
            Si ese usuario existe, le mandamos un mail con el enlace para elegir una contraseña
            nueva. Revisá también la carpeta de correo no deseado.
          </p>
        </div>

        <p className="text-[13px] leading-relaxed text-tinta-tenue">
          El enlace vale <strong>30 minutos</strong> y se puede usar una sola vez. Si no te llega,
          pedile a quien administra el sistema que te cambie la contraseña desde{' '}
          <strong>Configuración → Usuarios</strong>.
        </p>
      </div>
    )
  }

  return (
    <>
      <header className="mb-7">
        <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-tinta">
          ¿Olvidaste tu contraseña?
        </h1>
        <p className="mt-1.5 text-[14px] text-tinta-tenue">
          Poné tu mail y te mandamos un enlace para elegir una nueva.
        </p>
      </header>

      {error && <Aviso mensaje={error} />}

      <form onSubmit={enviar} className="space-y-4" noValidate>
        <Campo
          ref={refEmail}
          etiqueta="Email"
          type="email"
          autoComplete="username"
          inputMode="email"
          placeholder="tu@correo.com"
          icono={<Mail className="h-[18px] w-[18px]" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={enviando}
        />

        <button
          type="submit"
          className="btn-primario btn-toque mt-2 w-full"
          disabled={enviando || !email.trim()}
        >
          {enviando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
            </>
          ) : (
            <>
              Mandarme el enlace <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </>
  )
}

// ── Paso 2: elegir la contraseña ───────────────────────────────────────────

function PasoContrasenaNueva({ token, slug }: { token: string; slug: string }) {
  const navegar = useNavigate()

  const [password, setPassword] = useState('')
  const [repetida, setRepetida] = useState('')
  const [ver, setVer] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errores, setErrores] = useState<Record<string, string[]>>({})
  const [listo, setListo] = useState(false)

  const refPassword = useRef<HTMLInputElement>(null)

  useEffect(() => {
    refPassword.current?.focus()
  }, [])

  // Las dos tienen que coincidir, y se chequea ACÁ y no en el servidor: es un
  // error de tipeo, no una regla de negocio, y el servidor solo recibe una de
  // las dos. Que la segunda esté vacía todavía no es un error: recién cuenta
  // cuando hay algo escrito.
  const noCoinciden = repetida.length > 0 && password !== repetida

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (enviando || noCoinciden) return

    setEnviando(true)
    setError(null)
    setErrores({})

    try {
      await api.post('/auth/recuperar/cambiar', { token, passwordNueva: password })
      setListo(true)
    } catch (err) {
      const e = err as ErrorApi
      setError(mensajeDeError(err, 'No se pudo cambiar la contraseña.'))
      if (e.errores) setErrores(e.errores)
    } finally {
      setEnviando(false)
    }
  }

  if (listo) {
    return (
      <div>
        <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-verde/25 bg-verde/[0.06] px-3.5 py-3">
          <CheckCircle2 className="mt-0.5 h-[18px] w-[18px] shrink-0 text-verde" />
          <p className="text-[13.5px] leading-snug text-tinta">
            Listo. Ya podés entrar con la contraseña nueva.
          </p>
        </div>

        {/* Se avisa que las otras sesiones se cerraron, porque si no el que
            recuperó la contraseña desde el celular no entiende por qué la
            tablet del mostrador le pide entrar de nuevo. */}
        <p className="mb-5 text-[13px] leading-relaxed text-tinta-tenue">
          Por seguridad cerramos las sesiones que tenías abiertas en otros dispositivos: en cada
          uno hay que entrar otra vez.
        </p>

        <button
          type="button"
          className="btn-primario btn-toque w-full"
          onClick={() => void navegar(`/${slug}/login`, { replace: true })}
        >
          Entrar <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <>
      <header className="mb-7">
        <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-tinta">
          Elegí una contraseña nueva
        </h1>
        <p className="mt-1.5 text-[14px] text-tinta-tenue">
          Al menos 8 caracteres. Vas a tener que entrar de nuevo en los demás dispositivos.
        </p>
      </header>

      {error && <Aviso mensaje={error} />}

      <form onSubmit={enviar} className="space-y-4" noValidate>
        <Campo
          ref={refPassword}
          etiqueta="Contraseña nueva"
          type={ver ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder="••••••••"
          icono={<Lock className="h-[18px] w-[18px]" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errores.passwordNueva?.[0]}
          disabled={enviando}
          sufijo={
            <button
              type="button"
              onClick={() => setVer((v) => !v)}
              className="rounded p-2 text-tinta-tenue transition-colors hover:text-tinta"
              aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              tabIndex={-1}
            >
              {ver ? (
                <EyeOff className="h-[18px] w-[18px]" />
              ) : (
                <Eye className="h-[18px] w-[18px]" />
              )}
            </button>
          }
        />

        <Campo
          etiqueta="Repetila"
          type={ver ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder="••••••••"
          icono={<Lock className="h-[18px] w-[18px]" />}
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          error={noCoinciden ? 'Las dos contraseñas no son iguales.' : undefined}
          disabled={enviando}
        />

        <button
          type="submit"
          className="btn-primario btn-toque mt-2 w-full"
          disabled={enviando || password.length < 8 || noCoinciden || repetida.length === 0}
        >
          {enviando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
            </>
          ) : (
            <>
              Guardar y entrar <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </>
  )
}

function Aviso({ mensaje }: { mensaje: string }) {
  return (
    <div
      role="alert"
      className="mb-5 flex items-start gap-2.5 rounded-lg border border-rojo/25 bg-rojo/[0.06] px-3.5 py-3"
    >
      <AlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-rojo" />
      <p className="text-[13.5px] leading-snug text-tinta">{mensaje}</p>
    </div>
  )
}
